import React, { useEffect } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { useLocation } from 'react-router'

import { Loader } from '@ui'
import { AppContainer } from '@core'
import { ModalProvider } from '@app/util/modal/modal-context'
import { ManagerAssistantColumn } from '@app/component/core/manager-assistant/manager-assistant-column'
import { useIsManagerAssistantVisible } from '@app/component/core/manager-assistant/use-is-manager-assistant-visible'
import {
  miscUtil,
  useAccessRole
} from '@app/util'
import { PERMISSIONS } from '@app/const'
import { NotFound } from './NotFound'
import { useRoutingConfig } from '@app/routing'
import { setTitleAndMetaDescription } from './helper'
import connect from './connect'
import { useIsUsiningTiers } from '@app/util/hooks/tiers-hooks'
import { useOnboarding } from '@app/util/hooks/use-onboarding'
import { OnboardingProvider } from '@app/component/core/onboarding/onboarding-provider'
import { OnboardingTourLauncher } from '@app/component/core/onboarding/onboarding-tour-launcher'
import { ssoCallbackParamOf } from '@app/component/pages/auth/sso-callback/params'
import { lazyWithRetry } from '@app/util/lazy-with-retry'
import { isDevApi } from '@app/const/config'

const Login = lazyWithRetry(() => import('../component/pages/auth/login'))
const SsoCallback = lazyWithRetry(() => import('../component/pages/auth/sso-callback'))

// The route components are built inside render, so without this every render of Routes
// handed React a brand-new lazy component for the page already on screen. React does not
// remount over that — it reconciles a lazy against the type it resolves to — but the new
// one has to re-run its `import()` first, and that suspends the boundary below for a frame
// and flashes the loader over a page that never went anywhere. Keyed by componentPath, so
// one page is always one component.
const lazyPages = new Map()

const lazyPage = (componentPath) => {
  if (!lazyPages.has(componentPath)) {
    lazyPages.set(componentPath, lazyWithRetry(() => import(`../component/pages/${componentPath}`)))
  }
  return lazyPages.get(componentPath)
}

const Routes = ({
  auth,
  isLoading,
  isLoadingOrganization,
  setModal,
  workspaceId,
  workspace,
  isPluginEnabled,
  subscription,
  me,
  calendarDate,
  calendarView,
  routeChange,
  getOrganization,
  organization,
  isOrgOwner
}) => {
  const routes = useRoutingConfig({
    auth,
    isPluginEnabled,
    setModal,
    workspaceId,
    workspace,
    me,
    subscription,
    isOrgOwner
  })

  const location = useLocation()
  const isOrg = miscUtil.isDayswapsProInterface()
  const currentRoutes = routes[isOrg ? 'pro' : 'app']
  const allAllowedRoutes = routes.additional.concat(currentRoutes).filter((item) => item.allowed)
  const itemsInUrl = location.pathname.split('/').filter((i) => !!i).length
  const isOnSubpage = isOrg ? (itemsInUrl > 2) : (itemsInUrl > 1)
  const { canWrite } = useAccessRole()
  const isManagerAssistantVisible = useIsManagerAssistantVisible()
  const { shouldLand: shouldLandOnOnboarding } = useOnboarding()

  const isTrial = subscription?.subscriptionStatus === 'trial'
  const isNotPaidAndNotTrial = subscription?.subscriptionStatus === 'expired'
  const seatLimit = organization?.seatLimit || 0
  const employeeCount = organization?.employeeCount || 0
  const isUsingTiers = useIsUsiningTiers()
  const remainingSeats = seatLimit - employeeCount
  // Seat limits come from the Stripe subscription, which DEV organizations do not have:
  // seatLimit reads 0 there, so every tiered organization looks over its licence count and
  // gets locked behind the paywall. Ignore the licence-count condition on the DEV API.
  const notEnoughSeats = !isDevApi() && !isTrial && isUsingTiers && remainingSeats < 0

  const decideRedirect = () => {
    // no auth -> /login
    if (!auth) return '/login'

    // user has no password yet -> /registration/complete.
    // An SSO account is exempt: having no password is the design there, not something owed,
    // so sending it here asked people to invent one right after signing in with Google — and
    // because they then had one, `authLookup.ssoOnly` could never be true again, which made
    // the passwordless entry step unreachable. `hasPassword` alone cannot tell the two apart.
    if (!me.hasPassword && !me.hasSsoIdentity) {
      return '/registration/complete'
    }

    // manager/admin on unpaid WS -> /paywall
    if (subscription && (isNotPaidAndNotTrial || notEnoughSeats) && (canWrite(PERMISSIONS.SUBSCRIPTION) || isOrgOwner)) {
      if (isOrg) return '/organization/paywall'
      return '/paywall'
    }

    // manager of a brand-new workspace who has not skipped the checklist -> /onboarding.
    // Deliberately below the paywall check (an unpaid workspace has a more urgent screen)
    // and gated on `shouldLand` rather than on the route's own `allowed`, so that skipping
    // stops the landing while leaving the route and its nav item reachable. `!isOrg`
    // because the checklist is a workspace route: the DS PRO interface only registers
    // `routes.pro`, so redirecting there would land on NotFound.
    if (!isOrg && shouldLandOnOnboarding) return '/onboarding'

    // basic app paths being tried by priority. `/dashboard` comes first: its own gate is
    // `isPaidOrTrial && canRead(PERMISSIONS.MNG_DASHBOARD)`, so it is picked exactly for the
    // managers who have the tab, and everyone else falls through to the schedule as before.
    const currentlyAllowedAppRoutes = routes.app.filter((item) => item.allowed)
    const appPathsByPriority = ['/dashboard', '/schedule', '/workspace', '/dashboard-emp', '/my-timetable', '/backoffice']
    for (const p of appPathsByPriority) {
      if (currentlyAllowedAppRoutes.some(all => all.path === p)) {
        if (p === '/schedule') {
          return '/schedule/'
        }
        return p
      }
    }

    // DS PRO paths, if some are allowed
    const currentlyAllowedProRoutes = routes.pro.filter((item) => item.allowed)
    if (currentlyAllowedProRoutes.length) {
      return '/organization' + currentlyAllowedProRoutes[0].path
    }

    // fallback -> /no-workspace
    return '/no-workspace'
  }
  useEffect(() => {
    setTitleAndMetaDescription(location.pathname)
    routeChange()

    // when we switch to DS PRO, we must call getOrganization()
    if (location.pathname.includes('/organization') && !isLoadingOrganization && !organization?.id) {
      getOrganization()
    }
  }, [location.pathname])

  if (isLoading) return <Loader size={Loader.SIZES.LARGE} />

  // Since ssoBegin carries a returnTo, the OAuth callback comes back to whichever page
  // started the hop — any allow-listed path, not a fixed /auth/sso — so the five ?sso*
  // parameters are read here rather than on each screen that offers an SSO button.
  // It has to be above the <Switch>: the `/` redirect below drops the query string, so
  // a callback landing on the root would lose its parameter before anything could read
  // it. `?ssoRegister=` is forwarded to /registration and then left alone; see params.ts.
  const ssoCallbackParam = ssoCallbackParamOf(location)
  if (ssoCallbackParam) {
    return (
      <React.Suspense fallback={<Loader size={Loader.SIZES.LARGE} />}>
        <SsoCallback param={ssoCallbackParam} />
      </React.Suspense>
    )
  }

  return (
    // ModalProvider used to live in AppContainer, but that is rendered per-<Route>. The
    // assistant column is mounted outside the <Switch> and its employee links call
    // useModal(), which throws without a provider — so the provider is hoisted here, above
    // both. ModalContainer stays in AppContainer; it reads the layers from this context.
    <ModalProvider>
      {/* Mounted OUTSIDE the <Switch> (and outside Suspense) on purpose: AppContainer is
          rendered per-<Route>, so the whole app shell unmounts on every navigation. The
          assistant column has to outlive that or the conversation would be thrown away
          each time the manager changes page. */}
      {isManagerAssistantVisible && <ManagerAssistantColumn />}
      {/* Same reason for living out here: it starts the step's Intercom tour on the page
          the manager lands on, which the checklist page is no longer mounted to see. */}
      <OnboardingTourLauncher />
      {/* And the same again for the checklist's own state: „Vyřešit" sends the manager to
          the step's page, so the „step finished" toast has to be watched for from above the
          <Switch>. It also owns the query the checklist page then reads, so there is exactly
          one of each. Wraps <Switch> rather than sitting next to it because of that. */}
      <OnboardingProvider>
        <React.Suspense fallback={<Loader size={Loader.SIZES.LARGE} />}>
          <Switch>
            {!auth && <Route path='/login' component={Login} />}
            {allAllowedRoutes.map((route) => {
              const allowedSubPages = route.subpages && route.subpages.filter((item) => item.allowed)
              const firstPath = allowedSubPages && route.path + allowedSubPages[0]?.path
              const LazyComp = route.componentPath && lazyPage(route.componentPath)
              return (
                <Route
                  key={route.path}
                  path={isOrg ? '/organization' + route.path : route.path}
                  exact={!!route.exact}
                >
                  {!isOnSubpage && !!route.subpages && <Redirect to={isOrg ? '/organization' + firstPath : firstPath} />}
                  {route.componentPath && (
                    <AppContainer {...route.containerConfig}>
                      <LazyComp location={location} {...route} />
                    </AppContainer>
                  )}
                  {route.component && (
                    <AppContainer {...route.containerConfig}>
                      <route.component location={location} {...route} />
                    </AppContainer>
                  )}
                  {route.subpages && (
                    <AppContainer {...route.containerConfig}>
                      <Switch>
                        {route.subpages && route.subpages.map((sub) => {
                          const LazySubComp = lazyPage(sub.componentPath)
                          return (
                            <Route
                              location={location}
                              key={sub.path}
                              path={isOrg ? '/organization' + route.path + sub.path : route.path + sub.path}
                              exact={!!sub.exact}
                              component={LazySubComp}
                            />
                          )
                        })}
                      </Switch>
                    </AppContainer>
                  )}
                </Route>
              )
            })}
            <Route exact path={['/', '/login', '/paywall', '/organization']}>
              <Redirect to={decideRedirect()} />
            </Route>
            {/* The workspace selector is offered on the checklist too, and switching
                workspace does not navigate — so a switch to a workspace with no checklist
                leaves the URL on /onboarding with the route no longer in
                `allAllowedRoutes`. Without this it would fall through to NotFound below.
                Sits after the mapped routes, so while the workspace IS eligible the real
                onboarding route still wins. */}
            <Route path='/onboarding'>
              <Redirect to={decideRedirect()} />
            </Route>
            <Route>
              {!auth ? <Redirect to='/login' /> : (
                <AppContainer>
                  <NotFound isOrg={isOrg} />
                </AppContainer>
              )}
            </Route>
          </Switch>
        </React.Suspense>
      </OnboardingProvider>
    </ModalProvider>
  )
}

export default connect(Routes)
